/**
 * What counts as a Fitness Village, and what counts as one of its zones or courts, expressed once.
 *
 * A village is a `Branch`. Inside it, the lists follow the same type / subtype rule every
 * calendar uses (`shouldConsiderThisService`):
 *   zones  = active `class` Services with subType `fitness` (the `/Class` calendar's set)
 *   courts = active `standard` Services with no subType (the `/calendar` page's set)
 * Anything else on the branch — kids, pods, school, fun, gym, ticket-event, coach — stays off
 * both tabs. The home picker, the header nav and the village page all import from here, so
 * they cannot disagree about which branches are villages.
 */
import { shouldConsiderThisService } from '../components/Calendar/ServiceSelector/hooks/useServiceSelector';
import { Branch, Service } from '../hooks/useCompany';
import { compareFitnessVillagesByDisplayOrder } from './fitnessVillages';

/** Services are active unless the backend says otherwise; an absent flag is not "inactive". */
const isActiveService = (service: Service) => service.isActive !== false;

const branchServices = (servicesMap: Map<string, Service> | undefined, branchId?: string) =>
  branchId
    ? Array.from(servicesMap?.values() ?? []).filter(
        service => service.branchId === branchId && isActiveService(service)
      )
    : [];

export const isVillageZoneService = (service: Service): boolean =>
  shouldConsiderThisService(service, 'class', 'fitness');

export const isVillageCourtService = (service: Service): boolean =>
  shouldConsiderThisService(service, 'standard');

/** The zones on the Classes tab of one village. */
export const selectVillageZoneServices = (
  servicesMap: Map<string, Service> | undefined,
  branchId?: string
): Service[] => branchServices(servicesMap, branchId).filter(isVillageZoneService);

/** The court sports on the Courts tab of one village. */
export const selectVillageCourtServices = (
  servicesMap: Map<string, Service> | undefined,
  branchId?: string
): Service[] => branchServices(servicesMap, branchId).filter(isVillageCourtService);

/** Every branch with a zone or a court, in the client's display order (Kite, Warqa'a, Zabeel). */
export const selectFitnessVillages = (
  branchesMap: Map<string, Branch> | undefined,
  servicesMap: Map<string, Service> | undefined
): Branch[] =>
  Array.from(branchesMap?.values() ?? [])
    .filter(branch =>
      branchServices(servicesMap, branch.id).some(
        service => isVillageZoneService(service) || isVillageCourtService(service)
      )
    )
    .sort((left, right) => compareFitnessVillagesByDisplayOrder(left.name, right.name));
